import { useState, useMemo, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { useFilters, useSortBy, useTable, usePagination } from "react-table";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";

import { dispatch } from "../../../state/store";
import { setUserToEdit } from "../../../state/ducks";

import { marketNames, routePaths, statusNames, strCode, userTypeNames } from "../../../consts";
import { appUserService, t } from "../../../services";
import { adminUserUtils } from "../../../utils";
import { useTableData } from "../../../hooks";

import {
  Table,
  WrapperContainer,
  SearchInput,
  FlexContainers,
  TablePagination,
  Paragraph2,
  SVG,
  DoubleButton,
  IconTextButton
} from "../../../components";

import { UsersTableColumns } from "./UsersTableColumns";
import UserBatchCreateModal from "../UserBatchCreateModal/UserBatchCreateModal";
import Stores from "../../Stores/Stores";

import {
  TableFilersWrapper,
  SearchInputWrapper,
  TableFiltersRightWrapper,
  StoresButton,
  UsersTableWrapper,
  FilterButton,
  FilterCounter
} from "./styled";
import FiltersTab from "./components/FiltersTab/FiltersTab";
import useDeepCompareEffect from "use-deep-compare-effect";

const UsersTable = ({
  usersData = [],
  onImportUsersFile,
  onSaveStore,
  onDeleteStore,
  onMarketSelected
}) => {
  const [searchValue, setSearchValue] = useState("");

  const [batchModalActive, setBatchModalActive] = useState(false);

  const loading = useSelector((store) => store.users?.loading);
  const error = useSelector((store) => store.users?.error);

  const [storesModalActive, setStoresModalActive] = useState(false);

  const locations = useSelector((store) => store.configs.locations);

  const {
    markets: marketsFilter,
    status: statusFilter,
    type: typeFilter
  } = useSelector((store) => store.users.userFilters);

  const isAdmin = appUserService.isAdmin();

  const [marketOption, setMarketOption] = useState(null);

  const [filtersActive, setFiltersActive] = useState(false);
  const [activeFilters, setActiveFilters] = useState(0);

  const history = useHistory();

  const typeOptions = useMemo(
    () => [
      { id: userTypeNames.all, value: t(strCode.ALL_FILTER_BTN), count: usersData.length },
      {
        id: userTypeNames.regular,
        value: t(strCode.REGULAR_USER),
        count: usersData.filter((x) => !adminUserUtils.isUserAdmin(x)).length
      },
      {
        id: userTypeNames.admin,
        value: t(strCode.ADMIN_USER),
        count: usersData.filter((x) => adminUserUtils.isUserAdmin(x)).length
      }
    ],
    [usersData]
  );

  const users = useMemo(
    () =>
      usersData.filter((x) => {
        const isAdmin = adminUserUtils.isUserAdmin(x);

        return typeFilter === userTypeNames.admin
          ? isAdmin
          : typeFilter === userTypeNames.regular
          ? !isAdmin
          : true;
      }),
    [usersData, typeFilter]
  );

  const usersMapCallback = (user) => {
    return {
      ...user,
      location: user.locationId
        ? locations[user.markets[0]]?.find((l) => l.id === user.locationId)
        : null
    };
  };

  const data = useTableData({
    loading,
    data: users,
    loadingPlaceholdersRows: 10,
    mapCallback: usersMapCallback
  });
  const columns = useMemo(() => UsersTableColumns, []);
  const tableInstance = useTable(
    {
      columns,
      data,
      initialState: {
        sortBy: [
          {
            id: "email"
          }
        ]
      }
    },
    useFilters,
    useSortBy,
    usePagination
  );
  const {
    rows, // total rows
    setFilter,
    nextPage, // pagination props
    previousPage,
    canNextPage,
    canPreviousPage,
    gotoPage,
    pageCount,
    setPageSize,
    state: { pageIndex, pageSize }
  } = tableInstance;

  useEffect(() => {
    if (searchValue) {
      setFilter("email", searchValue);
    }

    if (statusFilter) {
      setFilter(
        "active",
        statusFilter === statusNames.allStatuses
          ? undefined
          : statusFilter === statusNames.active
          ? true
          : false
      );
    }
  }, [users, searchValue, statusFilter]);

  useDeepCompareEffect(() => {
    if (!marketsFilter) {
      return;
    }

    if (marketsFilter.length === 1 && marketsFilter[0] !== marketNames.allMarkets) {
      setMarketOption({ value: marketsFilter[0], id: marketsFilter[0] });
      onMarketSelected(marketsFilter[0]);
    } else {
      setMarketOption(null);
    }
  }, [marketsFilter ?? []]);

  const handleFilterChange = (value) => {
    setFilter("email", value);
    setSearchValue(value);
  };

  const onCreateUser = () => {
    history.push(routePaths.USERS_CREATE);
  };

  const onCreateIAPUser = () => {
    history.push(routePaths.USERS_CREATE_ADMIN);
  };

  const onCreateBatch = () => {
    setBatchModalActive(true);
  };

  const onEdit = (row) => {
    if (row?.email) {
      dispatch(setUserToEdit(row));

      const isAdminUser = adminUserUtils.isUserAdmin(row);
      history.push(
        `${isAdminUser ? routePaths.USERS_EDIT_ADMIN : routePaths.USERS_EDIT}/${row.email}`
      );
    }
  };

  return (
    <WrapperContainer mt={24} width="100%">
      <TableFilersWrapper mb={8} Container={FlexContainers.FlexRowBetween} width="100%" gap={8}>
        <SearchInputWrapper width={"100%"} maxWidth={329}>
          <SearchInput
            id="users-search"
            value={searchValue}
            setValue={handleFilterChange}
            placeholder={t(strCode.SEARCH_BY_USERNAME_PLACEHOLDER)}
          />
        </SearchInputWrapper>

        <FilterButton active={filtersActive} onClick={() => setFiltersActive((prev) => !prev)}>
          <SVG.TuneIcon />
          {activeFilters > 0 && <FilterCounter>{activeFilters}</FilterCounter>}
        </FilterButton>

        <TableFiltersRightWrapper Container={FlexContainers.FlexRowEnd} gap={8} width={"100%"}>
          <WrapperContainer Container={FlexContainers.FlexRow} gap={16}>
            {marketOption && (
              <StoresButton secondary onClick={() => setStoresModalActive(true)}>
                <SVG.LocationIcon />
                {t(strCode.LOCATIONS_TITLE)}
              </StoresButton>
            )}

            <DoubleButton title={t(strCode.NEW_BTN)} onClick={onCreateUser}>
              <IconTextButton
                onClick={() => onCreateUser()}
                icon={<SVG.CreateUserIcon />}
                hoverIcon={<SVG.CreateUserFilledIcon />}>
                {strCode.USER_BTN}
              </IconTextButton>

              {isAdmin && (
                <IconTextButton
                  onClick={() => onCreateIAPUser()}
                  icon={<SVG.CreateIAPUserIcon />}
                  hoverIcon={<SVG.CreateIAPUserFilledIcon />}>
                  {strCode.IAP_USER_BTN}
                </IconTextButton>
              )}

              <IconTextButton
                onClick={() => onCreateBatch()}
                icon={<SVG.CreateBatchIcon />}
                hoverIcon={<SVG.CreateBatchFilledIcon />}>
                {strCode.BATCH_CREATE_BTN}
              </IconTextButton>
            </DoubleButton>
          </WrapperContainer>
        </TableFiltersRightWrapper>
      </TableFilersWrapper>
      <FiltersTab
        visible={filtersActive}
        setActiveFilters={setActiveFilters}
        typeOptions={typeOptions}
        loading={loading}
      />

      {error ? (
        <WrapperContainer
          Container={FlexContainers.FlexColumnCenter}
          width={"100%"}
          mt={200}
          textAlign={"center"}>
          <Paragraph2>{t(strCode.USERS_FETCH_ERROR)}</Paragraph2>
        </WrapperContainer>
      ) : (
        users && (
          <>
            <UsersTableWrapper mt={24}>
              <Table
                tableKey="users-table"
                tableInstance={tableInstance}
                searchedText={searchValue}
                searchInfo={`${t(strCode.NO_USERNAME_FOUND)} '${searchValue}'.`}
                searchCheckInfo={t(strCode.CHECK_USERNAME_MESSAGE)}
                onRowClick={onEdit}
                tooltipLabel={t(strCode.EDIT)}
                isLoading={loading}
                hasPagination
              />
            </UsersTableWrapper>

            <TablePagination
              tableKey="users-table"
              tableSize={rows?.length}
              pageIndex={pageIndex}
              pageSize={pageSize}
              setPageSize={setPageSize}
              canPreviousPage={canPreviousPage}
              canNextPage={canNextPage}
              onPrevPage={previousPage}
              onNextPage={nextPage}
              onFirstPage={() => gotoPage(0)}
              onLastPage={() => gotoPage(pageCount - 1)}
            />
          </>
        )
      )}

      <UserBatchCreateModal
        active={batchModalActive}
        setActive={setBatchModalActive}
        onSubmit={onImportUsersFile}
      />

      <Stores
        active={storesModalActive}
        setActive={setStoresModalActive}
        market={marketOption?.id}
        onSaveStore={onSaveStore}
        onDeleteStore={onDeleteStore}
      />
    </WrapperContainer>
  );
};

UsersTable.propTypes = {
  usersData: PropTypes.arrayOf(
    PropTypes.shape({
      email: PropTypes.string.isRequired,
      groups: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string.isRequired,
          activeFrom: PropTypes.number.isRequired,
          activeTo: PropTypes.number.isRequired
        })
      ).isRequired,
      markets: PropTypes.arrayOf(PropTypes.string).isRequired,
      active: PropTypes.bool.isRequired
    })
  ).isRequired,
  onImportUsersFile: PropTypes.func.isRequired,
  onSaveStore: PropTypes.func.isRequired,
  onDeleteStore: PropTypes.func.isRequired,
  onMarketSelected: PropTypes.func.isRequired
};

export default UsersTable;
